import PaymentReceiptPanel from './shared/PaymentReceiptPanel';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, Tooltip, Dialog, TablePagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';
import { PharmacyMetric, PharmacySelect } from './shared/PharmacyUI';
import ComprobantesChart from './shared/ComprobantesChart';
import './ComprobantesPago.css';


const MESES = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const fmtS  = (v) => `S/ ${Number(v||0).toLocaleString('es-PE',{minimumFractionDigits:2})}`;
const fmtFecha = (s) => {
  if (!s) return '';
  const [fecha] = String(s).split(/[T ]/);
  // Formato ISO: yyyy-mm-dd
  if (fecha.includes('-')) {
    const [yyyy, mm, dd] = fecha.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }
  // Formato peruano: dd/mm/yyyy — devolver directo
  return fecha;
};

// ── Helpers UI ────────────────────────────────────────────────────────────────
const Lbl = ({children}) => (
  <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.07em',textTransform:'uppercase',mb:0.5}}>{children}</Typography>
);

const Sel = ({value,onChange,children,w}) => <PharmacySelect value={value} onChange={onChange} w={w||'100%'}>{children}</PharmacySelect>;

const TH = ({children,center,right,w,sortKey,orden,onSort}) => {
  const activo = sortKey && orden?.campo===sortKey;
  return (
    <th onClick={sortKey?()=>onSort(sortKey):undefined}
      style={{
      padding:'9px 8px', width:w||'auto',
      textAlign:center?'center':right?'right':'left',
      fontSize:'0.6rem', fontWeight:700, color:activo?IOS.blue:IOS.label2, letterSpacing:'0.06em',
      textTransform:'uppercase', backgroundColor:IOS.surface,
      borderBottom:`1px solid ${IOS.separator}`, whiteSpace:'nowrap',
      position:'sticky', top:0, zIndex:1,
      cursor:sortKey?'pointer':'default', userSelect:'none',
    }}>
      <span style={{display:'inline-flex',alignItems:'center',gap:3,justifyContent:right?'flex-end':center?'center':'flex-start'}}>
        {children}
        {sortKey && (
          <svg width="8" height="10" viewBox="0 0 8 12" style={{flexShrink:0,opacity:activo?1:0.35}}>
            <path d="M4 0L7 4H1z" fill={activo&&orden.dir==='asc'?IOS.blue:IOS.label2}/>
            <path d="M4 12L1 8h6z" fill={activo&&orden.dir==='desc'?IOS.blue:IOS.label2}/>
          </svg>
        )}
      </span>
    </th>
  );
};

const Paginacion = ({total,pagina,setPagina,rpp,setRpp}) => <TablePagination component="div" count={total} page={Math.max(0,Math.min(pagina-1,Math.ceil(total/rpp)-1))} rowsPerPage={rpp} rowsPerPageOptions={[25,50,100,200]} onPageChange={(_,p)=>setPagina(p+1)} onRowsPerPageChange={e=>{setRpp(Number(e.target.value));setPagina(1);}} labelRowsPerPage="Filas" labelDisplayedRows={({from,to,count})=>from+'–'+to+' de '+count} showFirstButton showLastButton sx={{'.MuiTablePagination-toolbar':{flexWrap:'wrap',justifyContent:'flex-end'},'.MuiTablePagination-select':{borderRadius:2}}}/>;

const PillAfect = ({val}) => {
  const ok=val==='AFECTA';
  return (
    <Box sx={{display:'inline-flex',alignItems:'center',gap:0.5,px:0.8,py:0.2,borderRadius:'20px',
      bgcolor:ok?'#EDFAF2':'#FFF8E6',border:`1px solid ${ok?'#B7EFD0':'#FFE9A0'}`}}>
      <Box sx={{width:4,height:4,borderRadius:'50%',bgcolor:ok?IOS.green:IOS.orange,flexShrink:0}}/>
      <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:ok?'#1A6B3C':'#8B6914',whiteSpace:'nowrap'}}>{val}</Typography>
    </Box>
  );
};

const PillEstado = ({val}) => {
  const ok=(val||'').toUpperCase().startsWith('ACTIV');
  return (
    <Box sx={{display:'inline-flex',alignItems:'center',gap:0.5,px:0.8,py:0.2,borderRadius:'20px',
      bgcolor:ok?`${IOS.blue}12`:'#FFF0EF',border:`1px solid ${ok?`${IOS.blue}30`:'#FFD4D1'}`}}>
      <Box sx={{width:4,height:4,borderRadius:'50%',bgcolor:ok?IOS.blue:IOS.red,flexShrink:0}}/>
      <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:ok?IOS.blue:'#C0392B',whiteSpace:'nowrap'}}>{val}</Typography>
    </Box>
  );
};

const KpiCard = ({label,monto,color,icon}) => <PharmacyMetric label={label} value={fmtS(monto)} color={color} icon={icon} sub="Según los filtros aplicados"/>;

// Card móvil por fila
const RowCard = ({row,onVer,onImprimir,onAnular,puedeAnular}) => (
  <Box sx={{p:2,borderBottom:`1px solid ${IOS.separator}`}}>
    <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',mb:1}}>
      <Box sx={{flex:1,minWidth:0,mr:1}}>
        <Typography sx={{fontWeight:700,fontSize:'0.82rem',color:IOS.label,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{row.cliente}</Typography>
        <Typography sx={{fontSize:'0.68rem',color:IOS.label2,fontFamily:'monospace'}}>{row.serie}-{row.numero} · {fmtFecha(row.fecha)}</Typography>
      </Box>
      <Typography sx={{fontWeight:800,fontSize:'0.95rem',color:IOS.label,flexShrink:0}}>{fmtS(row.total)}</Typography>
    </Box>
    <Box sx={{display:'flex',gap:0.8,flexWrap:'wrap',mb:1.2}}>
      <PillAfect val={row.afectacion} />
      <PillEstado val={row.estado} />
      <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',bgcolor:IOS.bg,border:`1px solid ${IOS.separator}`}}>
        <Typography sx={{fontSize:'0.6rem',fontWeight:600,color:IOS.label2}}>{row.tipoCPE}</Typography>
      </Box>
      <Box sx={{display:'inline-flex',px:0.8,py:0.2,borderRadius:'20px',bgcolor:IOS.bg,border:`1px solid ${IOS.separator}`}}>
        <Typography sx={{fontSize:'0.6rem',fontWeight:600,color:IOS.label2}}>{row.fuente}</Typography>
      </Box>
    </Box>
    <Box sx={{display:'flex',gap:1}}>
      <Box component="button" onClick={onVer}
        sx={{flex:1,height:30,borderRadius:'8px',border:`1px solid ${IOS.blue}30`,bgcolor:`${IOS.blue}08`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5,'&:hover':{bgcolor:`${IOS.blue}14`}}}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.blue}}>Ver</Typography>
      </Box>
      <Box component="button" onClick={onImprimir}
        sx={{flex:1,height:30,borderRadius:'8px',border:`1px solid ${IOS.green}30`,bgcolor:`${IOS.green}08`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5,'&:hover':{bgcolor:`${IOS.green}14`}}}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.green}}>Imprimir</Typography>
      </Box>
      {puedeAnular && (
        <Box component="button" onClick={onAnular}
          sx={{flex:1,height:30,borderRadius:'8px',border:'1px solid #FFD4D1',bgcolor:'#FFF0EF',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:0.5,'&:hover':{bgcolor:'#FFD4D1'}}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
          <Typography sx={{fontSize:'0.7rem',fontWeight:700,color:IOS.red}}>Anular</Typography>
        </Box>
      )}
    </Box>
  </Box>
);

const Modal = ({open,onClose,children,w=420}) => <Dialog open={Boolean(open)} onClose={onClose} maxWidth={false} PaperProps={{className:'cpe-modal',sx:{width:w,maxWidth:'calc(100vw - 24px)',borderRadius:'24px',m:1.5}}}>{children}</Dialog>;

const ModalHeader = ({title,onClose,icon,color=IOS.blue}) => (
  <Box sx={{px:2.5,pt:2.5,pb:2,borderBottom:`1px solid ${IOS.separator}`,
    display:'flex',alignItems:'center',justifyContent:'space-between'}}>
    <Box sx={{display:'flex',alignItems:'center',gap:1.2}}>
      <Box sx={{width:32,height:32,borderRadius:'10px',bgcolor:`${color}15`,
        display:'flex',alignItems:'center',justifyContent:'center'}}>{icon}</Box>
      <Typography sx={{fontWeight:700,fontSize:'0.92rem',color:IOS.label}}>{title}</Typography>
    </Box>
    <Box component="button" className="cpe-close" aria-label="Cerrar ventana" onClick={onClose}
      sx={{width:28,height:28,borderRadius:'50%',border:'none',bgcolor:'#E5E5EA',
        cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',
        '&:hover':{bgcolor:'#D1D1D6'}}}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
    </Box>
  </Box>
);

const OverlaySunat = ({ estado }) => {
  if (!estado) return null;
  const bg = estado==='ok' ? 'rgba(52,199,89,0.97)' : estado==='warning' ? 'rgba(255,149,0,0.97)' : 'rgba(0,0,0,0.88)';
  return (
    <Box sx={{position:'absolute',inset:0,zIndex:10,bgcolor:bg,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,borderRadius:'20px',transition:'background 0.5s ease'}}>
      {estado==='enviando'&&(
        <>
          <CircularProgress size={52} thickness={3} sx={{color:'rgba(255,255,255,0.9)'}}/>
          <Box sx={{textAlign:'center',px:3}}>
            <Typography sx={{color:'white',fontWeight:800,fontSize:'1.1rem',mb:0.5}}>Enviando a SUNAT...</Typography>
            <Typography sx={{color:'rgba(255,255,255,0.7)',fontSize:'0.78rem'}}>Firmando y enviando el comprobante electronico</Typography>
          </Box>
          <Box sx={{px:2,py:0.8,borderRadius:'20px',bgcolor:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)'}}>
            <Typography sx={{color:'rgba(255,255,255,0.9)',fontSize:'0.7rem',fontWeight:700,letterSpacing:'0.1em'}}>OSE · NUBEFACT</Typography>
          </Box>
        </>
      )}
      {estado==='ok'&&(
        <>
          <Box sx={{width:72,height:72,borderRadius:'50%',bgcolor:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
          </Box>
          <Box sx={{textAlign:'center',px:3}}>
            <Typography sx={{color:'white',fontWeight:800,fontSize:'1.2rem',mb:0.5}}>Aceptado por SUNAT</Typography>
            <Typography sx={{color:'rgba(255,255,255,0.85)',fontSize:'0.8rem'}}>Nota de credito emitida correctamente</Typography>
          </Box>
          <Box sx={{px:2,py:0.8,borderRadius:'20px',bgcolor:'rgba(255,255,255,0.2)',border:'1px solid rgba(255,255,255,0.3)'}}>
            <Typography sx={{color:'white',fontSize:'0.7rem',fontWeight:700,letterSpacing:'0.08em'}}>NC EMITIDA</Typography>
          </Box>
        </>
      )}
      {estado==='warning'&&(
        <>
          <Box sx={{width:72,height:72,borderRadius:'50%',bgcolor:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </Box>
          <Box sx={{textAlign:'center',px:3}}>
            <Typography sx={{color:'white',fontWeight:800,fontSize:'1.1rem',mb:0.5}}>Anulado en BD</Typography>
            <Typography sx={{color:'rgba(255,255,255,0.85)',fontSize:'0.78rem'}}>NC pendiente — reenviar desde Comprobantes</Typography>
          </Box>
          <Box sx={{px:2,py:0.8,borderRadius:'20px',bgcolor:'rgba(255,255,255,0.2)',border:'1px solid rgba(255,255,255,0.3)'}}>
            <Typography sx={{color:'white',fontSize:'0.7rem',fontWeight:700,letterSpacing:'0.08em'}}>NC PENDIENTE</Typography>
          </Box>
        </>
      )}
    </Box>
  );
};


// ══════════════════════════════════════════════════════════════════════════════
const ComprobantesPago = ({modulo='economia'}) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const ueId = user?.ueIdEstablecimiento||user?.UeIdEstablecimiento||0;
  const anhoActual = new Date().getFullYear();
  const mesActual  = new Date().getMonth()+1;

  const [anho,setBusqueda_anho] = useState(anhoActual);
  const setAnho = setBusqueda_anho;
  const [mes,setMes]           = useState(mesActual);
  const [busqueda,setBusqueda] = useState('');
  const [cargando,setCargando] = useState(false);
  const [datos,setDatos]       = useState([]);

  const [filtroIpress,   setFiltroIpress]   = useState('TODOS');
  const [filtroFuente,   setFiltroFuente]   = useState('TODOS');
  const [filtroTipo,     setFiltroTipo]     = useState('TODOS');
  const [filtroFormaPago,setFiltroFormaPago]= useState('TODOS');
  const [filtroAfect]    = useState('TODOS');
  const [filtroEstado,   setFiltroEstado]   = useState('TODOS');
  const [filtroRapido,   setFiltroRapido]   = useState('TODOS');
  const [filtroCdr, setFiltroCdr] = useState('TODOS');
  const [pagina,setPagina] = useState(1);

  const [rpp,setRpp]       = useState(50);
  const [orden,setOrden]   = useState({campo:null,dir:'asc'});

  const toggleOrden = (campo) => {
    setPagina(1);
    setOrden(prev=>{
      if(prev.campo!==campo) return {campo,dir:'desc'};
      return {campo,dir:prev.dir==='desc'?'asc':'desc'};
    });
  };

  const [datosGrafico,setDatosGrafico] = useState({dias:[],afecto:[],inafecto:[]});
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);
  const [tipoGrafico, setTipoGrafico] = useState('line');

  useEffect(()=>{
    setPagina(1);
  },[busqueda,diaSeleccionado,filtroRapido,filtroIpress,filtroFuente,filtroTipo,filtroFormaPago,filtroAfect,filtroEstado,filtroCdr]);

  const [panelOpen,setPanelOpen]       = useState(false);

  useEffect(()=>{
    document.body.style.overflow = panelOpen ? 'hidden' : '';
    return ()=>{ document.body.style.overflow=''; };
  },[panelOpen]);
  const [filaSelec,setFilaSelec]       = useState(null);
  const [detalleVenta,setDetalleVenta] = useState([]);
  const [cargandoDet,setCargandoDet]   = useState(false);
  const [cdrDisponible,setCdrDisponible] = useState(false);
  const [xmlDisponible,setXmlDisponible] = useState(false);
  const [descargandoDoc,setDescargandoDoc] = useState(null); // 'xml' | 'cdr' | null

  const [modalAnular,  setModalAnular]  = useState(false);
  const [anulando,     setAnulando]     = useState(false);
  const [modalExportar,setModalExportar]= useState(false);
  const [exportando,   setExportando]   = useState(false);
  const [modalFormato, setModalFormato] = useState(false);
  const [filaImprimir, setFilaImprimir] = useState(null);
  const [imprimiendo,  setImprimiendo]  = useState(false);

  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const [estadoCpeAnular, setEstadoCpeAnular] = useState(null); // null | 'enviando' | 'ok' | 'warning'

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const r=await api.get('/economia/ComprobantesPago/consolidado',{params:{periodo:anho,mes,ueId}});
      if(r.data.success) setDatos(r.data.data);
    } catch(e){console.error(e);} finally{setCargando(false);}
  },[anho,mes,ueId]);

  const cargarGrafico = useCallback(async () => {
    try {
      const r=await api.get('/economia/ComprobantesPago/afectacion-diaria',{params:{periodo:anho,mes,ueId}});


if(r.data.success){
  const rows = r.data.data;

  setDatosGrafico({
    dias:     rows.map(x => String(x.Dia || x.dia || '').padStart(2,'0')),
    afecto:   rows.map(x => Number(x.TotalAfecto   || x.totalAfecto   || 0)),
    inafecto: rows.map(x => Number(x.TotalInafecto || x.totalInafecto || 0)),
  });
}
    } catch(e){console.error(e);}
  },[anho,mes,ueId]);

  useEffect(()=>{
  setDiaSeleccionado(null);
  setFiltroCdr('TODOS'); // ← AGREGAR
  setPagina(1);          // ← AGREGAR también por buena práctica
  cargarDatos();
  cargarGrafico();
},[cargarDatos,cargarGrafico]);

  const optsIpress = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.establecimiento).filter(Boolean))],[datos]);
  const optsFuente = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.fuente).filter(Boolean))],[datos]);
  const optsTipo   = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.tipoCPE).filter(Boolean))],[datos]);
  const optsFP     = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.fp).filter(Boolean))],[datos]);
  const optsEstado = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.estado).filter(Boolean))],[datos]);
  const optsCdr    = useMemo(()=>['TODOS',...new Set(datos.map(d=>d.obsResumen).filter(Boolean))],[datos]);

  const datosFiltrados = useMemo(()=>{
  let f = datos;
  // Filtro texto local — serie, número, cliente, DNI
  if (busqueda.trim()) {
    const q = busqueda.trim().toLowerCase();
    f = f.filter(d =>
      (d.serie||'').toLowerCase().includes(q) ||
      (d.numero||'').toLowerCase().includes(q) ||
      (d.cliente||'').toLowerCase().includes(q) ||
      (d.dni||'').toLowerCase().includes(q)
    );
  }
  if (diaSeleccionado) {
    f = f.filter(d => {
      if (!d.fecha) return false;
      const [fechaParte] = d.fecha.split(' ');
      // Soporta dd/mm/yyyy e ISO yyyy-mm-dd
      const dd = fechaParte.includes('-')
        ? fechaParte.split('-')[2]
        : fechaParte.split('/')[0];
      return dd.padStart(2,'0') === diaSeleccionado;
    });
  }
  
    if(filtroRapido==='AFECTAS')   f=f.filter(d=>d.afectacion==='AFECTA');
    if(filtroRapido==='INAFECTAS') f=f.filter(d=>d.afectacion==='INAFECTA');
    if(filtroIpress   !=='TODOS') f=f.filter(d=>d.establecimiento===filtroIpress);
    if(filtroFuente   !=='TODOS') f=f.filter(d=>d.fuente===filtroFuente);
    if(filtroTipo     !=='TODOS') f=f.filter(d=>d.tipoCPE===filtroTipo);
    if(filtroFormaPago!=='TODOS') f=f.filter(d=>d.fp===filtroFormaPago);
    if(filtroAfect    !=='TODOS') f=f.filter(d=>d.afectacion===filtroAfect);
    if(filtroEstado   !=='TODOS') f=f.filter(d=>d.estado===filtroEstado);
    if(filtroCdr !== 'TODOS') {
  f = f.filter(d => 
  String(d.obsResumen || '').toUpperCase().includes(
    String(filtroCdr).toUpperCase()
  )
);
}
    return f;
  },[datos,busqueda,diaSeleccionado,filtroRapido,filtroIpress,filtroFuente,filtroTipo,filtroFormaPago,filtroAfect,filtroEstado,filtroCdr]);

  const kpis = useMemo(()=>{
    const normalize=v=>String(v||'').normalize('NFD').replace(/[\u0300-\u036f]/g,'').trim().toUpperCase();
    const groups=[
      ['Afectos','AFECTA',['BOLETA','TICKET','FACTURA'],IOS.green],
      ['Inafectos','INAFECTA',['BOLETA','TICKET','FACTURA'],IOS.orange],
      ['Notas de crédito · Afectas','AFECTA',['NOTA DE CREDITO','NC','07'],IOS.purple],
      ['Notas de crédito · Inafectas','INAFECTA',['NOTA DE CREDITO','NC','07'],IOS.red],
      ['Notas de débito · Afectas','AFECTA',['NOTA DE DEBITO','ND','08'],IOS.blue],
      ['Notas de débito · Inafectas','INAFECTA',['NOTA DE DEBITO','ND','08'],'#669DA8'],
    ];
    return groups.map(([title,affect,types,color])=>{
      const rows=datosFiltrados.filter(d=>normalize(d.afectacion)===affect&&types.includes(normalize(d.tipoCPE)));
      return {label:rows.length+' '+title,monto:rows.reduce((total,d)=>total+Number(d.total||0),0),color,icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>};
    });
  },[datosFiltrados]);

  // Gráfico con gradiente bonito
  const verDetalle = async (fila) => {
    setFilaSelec(fila); setPanelOpen(true); setCargandoDet(true); setDetalleVenta([]);
    setCdrDisponible(false); setXmlDisponible(false);
    try {
      const r=await api.get('/economia/ComprobantesPago/detalle-venta',{
        params:{ idVenta:fila.codigo, tipoCPE:fila.tipoCPE, serie:fila.serie, numero:fila.numero, ueId }
      });
      if(r.data.success){
        setDetalleVenta(r.data.data);
        setCdrDisponible(!!r.data.cdrDisponible);
        setXmlDisponible(!!r.data.xmlOriginalDisponible);
      }
    } catch(e){console.error(e);} finally{setCargandoDet(false);}
  };

  const descargarDocOriginal = async (tipo) => {
    if(!filaSelec) return;
    setDescargandoDoc(tipo);
    try {
      const endpoint = tipo==='xml' ? 'descargar-xml-original' : 'descargar-cdr';
      const params = { tipoCPE:filaSelec.tipoCPE, serie:filaSelec.serie, numero:filaSelec.numero, ueId };
      if(tipo==='xml'){ params.idVenta = filaSelec.codigo; params.modulo = filaSelec.fuente; }
      const r = await api.get(`/economia/ComprobantesPago/${endpoint}`, { params, responseType:'blob' });
      const nombre = tipo==='xml' ? `${filaSelec.serie}-${filaSelec.numero}.xml` : `R-${filaSelec.serie}-${filaSelec.numero}.xml`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data],{type:'application/xml'}));
      a.download = nombre; a.click(); URL.revokeObjectURL(a.href);
    } catch(e){
      console.error(e);
      const msg = e?.response?.data?.message || 'No se pudo descargar el documento.';
      alert(msg);
    }
    finally { setDescargandoDoc(null); }
  };

  const imprimir=(fila)=>{ setFilaImprimir(fila); setModalFormato(true); };

  const ejecutarImpresion = async (fmt) => {
    if(!filaImprimir) return; setImprimiendo(true);
    try {
      const r=await api.get('/economia/ComprobantesPago/imprimir',{params:{idVenta:filaImprimir.codigo,fuente:filaImprimir.fuente,tipoCPE:filaImprimir.tipoCPE,formato:fmt,ueId},responseType:'blob'});
      window.open(URL.createObjectURL(new Blob([r.data],{type:'application/pdf'})),'_blank');
      setModalFormato(false);
    } catch(e){console.error(e);} finally{setImprimiendo(false);}
  };

  const confirmarAnular = async () => {
  if (!filaSelec) return;
  const esNC = ['BOLETA','FACTURA'].includes((filaSelec.tipoCPE||'').toUpperCase());
  setAnulando(true);
  if (esNC) setEstadoCpeAnular('enviando');
  try {
    const r = await api.post('/economia/ComprobantesPago/anular-con-cpe', {
      idVenta:  parseInt(filaSelec.codigo),
      idMotivo: 1, // motivo por defecto — ajusta según tu BD
      tipoCpe:  filaSelec.tipoCPE,
      idUe:     ueId,
    });
    if (r.data.success) {
      const cpe = r.data.cpe;
      if (esNC && cpe) {
        setEstadoCpeAnular(cpe.emitido ? 'ok' : 'warning');
        setTimeout(() => {
          setEstadoCpeAnular(null);
          setModalAnular(false);
          setPanelOpen(false);
          setFilaSelec(null);
          cargarDatos();
        }, 2500);
      } else {
        setModalAnular(false);
        setPanelOpen(false);
        setFilaSelec(null);
        cargarDatos();
      }
    } else {
      setEstadoCpeAnular(null);
    }
  } catch(e) {
    setEstadoCpeAnular(null);
    console.error(e);
  } finally { setAnulando(false); }
};

  const exportarConFiltros = async (tipo) => {
    setExportando(true);
    try {
      const resp=await api.post(`/economia/ComprobantesPago/exportar-${tipo}`,{datos:datosFiltrados,periodo:anho,mes,mesNombre:MESES[mes-1],filtroRapido,filtroIpress,filtroFuente,filtroTipo,filtroFormaPago,filtroEstado,busqueda:busqueda||null},{responseType:'blob'});
      const ext=tipo==='excel'?'xlsx':'pdf';
      const mime=tipo==='excel'?'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':'application/pdf';
      const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([resp.data],{type:mime}));
      a.download=`Comprobantes_${MESES[mes-1]}_${anho}.${ext}`; a.click(); URL.revokeObjectURL(a.href);
      setModalExportar(false);
    } catch(e){console.error(e);} finally{setExportando(false);}
  };

  const puedeAnular=(row)=>{
    if(!row)return false;
    const e=(row.estado||'').toUpperCase();
    const t=(row.tipoCPE||'').toUpperCase();
    const f=(row.fuente||'').toUpperCase();
    if(f==='SISMED') return false; // las anulaciones SISMED se hacen en el software SISMED (devuelven stock)
    return !e.includes('ANULAD')&&!t.includes('NOTA');
  };
  const esSismed = (row) => (row?.fuente||'').toUpperCase()==='SISMED';
  const requiereCpe = (row) => ['BOLETA','FACTURA','NOTA DE CRÉDITO','NOTA DE CREDITO','NOTA DE DÉBITO','NOTA DE DEBITO'].includes((row?.tipoCPE||'').toUpperCase());

  const datosOrdenados = useMemo(()=>{
    if(!orden.campo) return datosFiltrados;
    const {campo,dir}=orden;
    const mult = dir==='asc'?1:-1;
    const keyFecha = v => {
      const s=String(v||'').split(' ')[0];
      if(s.includes('/')){ const [dd,mm,yy]=s.split('/'); return `${yy}${mm}${dd}`; }
      return s;
    };
    return [...datosFiltrados].sort((a,b)=>{
      if(campo==='fecha')
        return keyFecha(a.fecha).localeCompare(keyFecha(b.fecha))*mult;
      if(campo==='total')
        return (Number(a[campo]||0)-Number(b[campo]||0))*mult;
      const va=String(a[campo]??''), vb=String(b[campo]??'');
      return va.localeCompare(vb,'es',{numeric:true,sensitivity:'base'})*mult;
    });
  },[datosFiltrados,orden]);

  const datosPagina=datosOrdenados.slice((pagina-1)*rpp,pagina*rpp);

  return (
    <>
      <Box className="cpe-page" sx={{minHeight:'100vh',bgcolor:IOS.bg}}>
        <HeaderIOS titulo="Comprobantes" subtitulo="Economía"
          onHome={()=>navigate(`/${modulo}`)} onLogout={handleLogout}
          nombreCompleto={user?.nombreCompleto||user?.usuario||''}
          avatarLetra={(user?.nombreCompleto||user?.usuario||'U').charAt(0).toUpperCase()} />

        <Box className="cpe-content" sx={{maxWidth:1600,mx:'auto',px:{xs:1.5,sm:2.6},pt:2.5,pb:6}}>
          <Box className="cpe-intro"><Typography className="cpe-eyebrow">Comprobantes · Economía</Typography><Typography component="h1">Cada pago, en detalle</Typography><Typography>Consulta la recaudación, encuentra tus comprobantes y revisa cada operación.</Typography></Box>

          {/* KPIs */}
          <Box className="cpe-summary-grid">
            <Box className="cpe-summary-period" sx={{bgcolor:IOS.surface,borderRadius:'24px',p:2,boxShadow:'0 6px 24px rgba(32,52,78,0.045)',border:`1px solid ${IOS.separator}`}}>
              <Typography sx={{fontSize:12,fontWeight:650,color:IOS.label2,mb:1.5}}>Período</Typography>
              <Box className="cpe-period-controls cpe-period-compact">
                <input type="number" value={anho} onChange={e=>setAnho(Number(e.target.value))} min="2020" max="2099"
                  style={{width:62,height:34,padding:'0 6px',border:`1px solid ${IOS.separator}`,borderRadius:8,fontSize:'0.8rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',fontFamily:'inherit'}}
                  onFocus={e=>e.target.style.borderColor=IOS.blue}
                  onBlur={e=>e.target.style.borderColor=IOS.separator} />
                <Sel value={mes} onChange={e=>setMes(Number(e.target.value))}>
                  {MESES.map((m,i)=><option key={i+1} value={i+1}>{m.charAt(0)+m.slice(1).toLowerCase()}</option>)}
                </Sel>
              </Box>
            </Box>
            {kpis.map(k=><KpiCard key={k.label} {...k} />)}
          </Box>

          {/* Gráfico */}
          <Box sx={{bgcolor:IOS.surface,borderRadius:'24px',p:2.5,mb:2,boxShadow:'0 6px 24px rgba(32,52,78,0.045)',border:`1px solid ${IOS.separator}`}}>
            <Box sx={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',mb:2}}>
  <Box>
    <Typography sx={{fontWeight:700,fontSize:'0.88rem',color:IOS.label,mb:0.2}}>Tendencia de Recaudación</Typography>
    <Typography sx={{fontSize:'0.7rem',color:IOS.label2}}>Evolución diaria — {MESES[mes-1]} {anho}</Typography>
  </Box>
  <Box sx={{display:'flex',gap:0.4,bgcolor:IOS.bg,borderRadius:'10px',p:0.4,flexShrink:0}}>
    {[
      {key:'line',   tip:'Línea',      icon:<path d="M3 17l4-8 4 4 4-6 4 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>},
      {key:'bar',    tip:'Barras',     icon:<><rect x="3" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".9"/><rect x="10" y="6" width="4" height="12" rx="1" fill="currentColor" opacity=".9"/><rect x="17" y="3" width="4" height="15" rx="1" fill="currentColor" opacity=".9"/></>},
      {key:'stacked',tip:'Apilado',    icon:<><rect x="3" y="13" width="4" height="5" rx="1" fill="currentColor" opacity=".5"/><rect x="3" y="8" width="4" height="5" rx="1" fill="currentColor"/><rect x="10" y="10" width="4" height="8" rx="1" fill="currentColor" opacity=".5"/><rect x="10" y="5" width="4" height="5" rx="1" fill="currentColor"/><rect x="17" y="11" width="4" height="7" rx="1" fill="currentColor" opacity=".5"/><rect x="17" y="6" width="4" height="5" rx="1" fill="currentColor"/></>},
      {key:'scatter',tip:'Dispersión', icon:<><circle cx="5" cy="15" r="1.8" fill="currentColor"/><circle cx="9" cy="9" r="1.8" fill="currentColor"/><circle cx="13" cy="13" r="1.8" fill="currentColor"/><circle cx="17" cy="6" r="1.8" fill="currentColor"/><circle cx="20" cy="11" r="1.8" fill="currentColor"/></>},
    ].map(({key,tip,icon})=>(
      <Tooltip key={key} title={tip} placement="top">
        <Box component="button" aria-label={tip} aria-pressed={tipoGrafico===key} onClick={()=>setTipoGrafico(key)}
          sx={{width:30,height:30,borderRadius:'8px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s',
            bgcolor:tipoGrafico===key?IOS.surface:'transparent',
            boxShadow:tipoGrafico===key?'0 1px 3px rgba(0,0,0,0.12)':'none',
            color:tipoGrafico===key?IOS.blue:IOS.label2,
            '&:hover':{bgcolor:tipoGrafico===key?IOS.surface:IOS.separator+'40'}}}>
          <svg width="18" height="18" viewBox="0 0 24 24">{icon}</svg>
        </Box>
      </Tooltip>
    ))}
  </Box>
</Box>

<ComprobantesChart datos={datosGrafico} tipo={tipoGrafico} onDay={dia=>{setDiaSeleccionado(prev=>prev===dia?null:dia);setPagina(1);}}/>
            <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:2,flexWrap:'wrap',mt:2,pt:2,borderTop:'1px solid #edf1f6'}}>
              <Typography sx={{fontSize:12,color:IOS.label2}}>Selecciona un día para ver sus comprobantes.</Typography>
              <PharmacySelect aria-label="Filtrar comprobantes por día" w={180} value={diaSeleccionado||''} onChange={e=>{setDiaSeleccionado(e.target.value||null);setPagina(1);}}>
                <option value="">Todos los días</option>
                {[...new Set(datosGrafico.dias.map(d=>String(d).padStart(2,'0')))].sort().map(d=><option key={d} value={d}>Día {d}</option>)}
              </PharmacySelect>
            </Box>
          </Box>

          {/* Filtros */}
          <Box sx={{bgcolor:IOS.surface,borderRadius:'24px',p:2,mb:2,boxShadow:'0 6px 24px rgba(32,52,78,0.045)',border:`1px solid ${IOS.separator}`}}>
            <Box sx={{display:'flex',gap:1.5,flexWrap:'wrap',alignItems:'flex-end',mb:2}}>
              <Box sx={{flex:'1 1 200px',maxWidth:440}}>
                <Lbl>Buscar</Lbl>
                <Box sx={{position:'relative'}}>
                  <Box sx={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:IOS.label2}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                  </Box>
                  <input type="text" placeholder="Cliente, DNI, serie, correlativo..." value={busqueda}
                    onChange={e=>setBusqueda(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&cargarDatos()}
                    style={{width:'100%',height:34,paddingLeft:28,paddingRight:10,border:`1px solid ${IOS.separator}`,borderRadius:8,fontSize:'0.8rem',color:IOS.label,backgroundColor:IOS.surface,outline:'none',boxSizing:'border-box',fontFamily:'inherit'}}
                    onFocus={e=>e.target.style.borderColor=IOS.blue}
                    onBlur={e=>e.target.style.borderColor=IOS.separator} />
                </Box>
              </Box>
              <Box>
                <Lbl>Filtro rápido</Lbl>
                <Box sx={{display:'flex',gap:0.5,bgcolor:IOS.bg,borderRadius:'10px',p:0.4}}>
                  {['TODOS','AFECTAS','INAFECTAS'].map(v=>(
                    <Box component="button" key={v} aria-pressed={filtroRapido===v} onClick={()=>{setFiltroRapido(v);setPagina(1);}}
                      sx={{px:1.5,py:0.6,borderRadius:'8px',cursor:'pointer',transition:'all 0.15s',
                        bgcolor:filtroRapido===v?IOS.surface:'transparent',
                        boxShadow:filtroRapido===v?'0 1px 3px rgba(0,0,0,0.1)':'none'}}>
                      <Typography sx={{fontSize:'0.76rem',fontWeight:filtroRapido===v?700:500,color:filtroRapido===v?IOS.blue:IOS.label2,whiteSpace:'nowrap'}}>{v}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ml:'auto',display:'flex',alignItems:'flex-end',gap:1}}>
                <Box sx={{px:1.5,py:0.6,bgcolor:IOS.bg,borderRadius:'20px',border:`1px solid ${IOS.separator}`,textAlign:'center'}}>
                  <Typography sx={{fontSize:'0.58rem',color:IOS.label2,display:'block'}}>Registros</Typography>
                  <Typography sx={{fontSize:'0.88rem',fontWeight:700,color:IOS.blue,lineHeight:1}}>{datosFiltrados.length.toLocaleString()}</Typography>
                </Box>
                <Tooltip title="Exportar" placement="top">
                  <Box component="button"
                    onClick={()=>datosFiltrados.length>0&&setModalExportar(true)}
                    sx={{width:42,height:42,borderRadius:'13px',border:'none',bgcolor:IOS.blue,
                      cursor:datosFiltrados.length===0?'default':'pointer',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      opacity:datosFiltrados.length===0?0.4:1,
                      transition:'all 0.15s','&:hover':{filter:'brightness(0.9)'}}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
                      <path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/>
                    </svg>
                  </Box>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{display:'flex',flexWrap:'wrap',gap:1.5,alignItems:'flex-end',pt:1.5,borderTop:`1px solid ${IOS.separator}`}}>
              {[['IPRESS',filtroIpress,setFiltroIpress,optsIpress],['Fuente',filtroFuente,setFiltroFuente,optsFuente],['Tipo CPE',filtroTipo,setFiltroTipo,optsTipo],['Forma Pago',filtroFormaPago,setFiltroFormaPago,optsFP],['Estado',filtroEstado,setFiltroEstado,optsEstado],['CDR',filtroCdr,setFiltroCdr,optsCdr]].map(([lbl,val,set,opts])=>(
                <Box key={lbl} sx={{flex:lbl==='IPRESS'?'2 1 240px':'1 1 150px',minWidth:0}}>
                  <Lbl>{lbl}</Lbl>
                  <Sel value={val} onChange={e=>{set(e.target.value);setPagina(1);}}>
                    {opts.map(o=><option key={o} value={o}>{o}</option>)}
                  </Sel>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Tabla / Cards */}
          {cargando
            ? <Box sx={{py:10,textAlign:'center'}}><CircularProgress sx={{color:IOS.blue}} /></Box>
            : <Box sx={{bgcolor:IOS.surface,borderRadius:'24px',boxShadow:'0 6px 24px rgba(32,52,78,0.045)',border:`1px solid ${IOS.separator}`,overflow:'hidden'}}>

                {/* Vista MÓVIL — cards */}
                <Box sx={{display:{xs:'block',md:'none'}}}>
                  {datosPagina.length===0
                    ? <Box sx={{py:6,textAlign:'center'}}><Typography sx={{color:IOS.label2,fontSize:'0.82rem'}}>Sin registros</Typography></Box>
                    : datosPagina.map((row,i)=>(
                        <RowCard key={i} row={row}
                          onVer={()=>verDetalle(row)}
                          onImprimir={()=>imprimir(row)}
                          onAnular={()=>{setFilaSelec(row);setModalAnular(true);}}
                          puedeAnular={puedeAnular(row)} />
                      ))
                  }
                </Box>

                {/* Vista DESKTOP — tabla */}
                <Box sx={{display:{xs:'none',md:'block'},overflowX:'auto',overflowY:'auto',maxHeight:'72vh',
                  '&::-webkit-scrollbar':{width:4,height:4},'&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4}}}>
                  <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed',minWidth:1150,fontSize:'0.75rem'}}>
                    <colgroup>
                      <col style={{width:120}}/><col style={{width:60}}/><col style={{width:65}}/><col style={{width:85}}/>
                      <col style={{width:50}}/><col style={{width:80}}/><col style={{width:88}}/><col style={{width:155}}/>
                      <col style={{width:85}}/><col style={{width:72}}/><col style={{width:78}}/><col style={{width:75}}/>
                      <col style={{width:105}}/><col style={{width:105}}/>
                      <col style={{width:32}}/><col style={{width:32}}/><col style={{width:32}}/>
                    </colgroup>
                    <thead>
                      <tr>
                        <TH sortKey="establecimiento" orden={orden} onSort={toggleOrden}>Establecimiento</TH>
                        <TH sortKey="fp" orden={orden} onSort={toggleOrden}>F.Pago</TH>
                        <TH sortKey="fuente" orden={orden} onSort={toggleOrden}>Fuente</TH>
                        <TH sortKey="tipoCPE" orden={orden} onSort={toggleOrden}>Tipo</TH>
                        <TH sortKey="serie" orden={orden} onSort={toggleOrden}>Serie</TH>
                        <TH sortKey="numero" orden={orden} onSort={toggleOrden}>Número</TH>
                        <TH sortKey="fecha" orden={orden} onSort={toggleOrden}>Fecha</TH>
                        <TH sortKey="cliente" orden={orden} onSort={toggleOrden}>Cliente</TH>
                        <TH right sortKey="dni" orden={orden} onSort={toggleOrden}>DNI</TH>
                        <TH right sortKey="total" orden={orden} onSort={toggleOrden}>Total</TH>
                        <TH center sortKey="afectacion" orden={orden} onSort={toggleOrden}>Afect.</TH>
                        <TH center sortKey="estado" orden={orden} onSort={toggleOrden}>Estado</TH>
                        <TH>Observación</TH><TH>CDR</TH>
                        <TH center></TH><TH center></TH><TH center></TH>
                      </tr>
                    </thead>
                    <tbody>
                      {datosPagina.length===0
                        ? <tr><td colSpan={17} style={{padding:'40px',textAlign:'center',color:IOS.label2,fontSize:'0.82rem'}}>Sin registros para mostrar</td></tr>
                        : datosPagina.map((row,i)=>(
                          <tr key={i}
                            style={{borderBottom:`1px solid ${IOS.separator}`,backgroundColor:IOS.surface,transition:'background 0.1s'}}
                            onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                            onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                            <td style={{padding:'7px 8px'}}><span style={{display:'block',whiteSpace:'normal',overflowWrap:'anywhere',color:IOS.label,fontSize:'0.72rem'}}>{row.establecimiento}</span></td>
                            <td style={{padding:'7px 8px',color:IOS.label2,fontSize:'0.7rem'}}>{row.fp}</td>
                            <td style={{padding:'7px 8px',color:IOS.label2,fontSize:'0.7rem'}}>{row.fuente}</td>
                            <td style={{padding:'7px 8px',color:IOS.label,fontSize:'0.7rem'}}>{row.tipoCPE}</td>
                            <td style={{padding:'7px 8px',fontFamily:'monospace',color:IOS.label2,fontSize:'0.68rem'}}>{row.serie}</td>
                            <td style={{padding:'7px 8px',fontFamily:'monospace',color:IOS.label2,fontSize:'0.68rem'}}>{row.numero}</td>
                            <td style={{padding:'7px 8px',color:IOS.label2,whiteSpace:'nowrap',fontSize:'0.7rem'}}>{fmtFecha(row.fecha)}</td>
                            <td style={{padding:'7px 8px'}}><span style={{display:'block',whiteSpace:'normal',overflowWrap:'anywhere',color:IOS.label,fontSize:'0.72rem'}}>{row.cliente}</span></td>
                            <td style={{padding:'7px 8px',textAlign:'right',fontFamily:'monospace',color:IOS.label2,fontSize:'0.68rem'}}>{row.dni}</td>
                            <td style={{padding:'7px 8px',textAlign:'right',fontWeight:700,color:IOS.label,fontSize:'0.72rem'}}>{Number(row.total||0).toFixed(2)}</td>
                            <td style={{padding:'7px 8px',textAlign:'center'}}><PillAfect val={row.afectacion} /></td>
                            <td style={{padding:'7px 8px',textAlign:'center'}}><PillEstado val={row.estado} /></td>
                            <td style={{padding:'7px 8px'}}><span style={{display:'block',whiteSpace:'normal',overflowWrap:'anywhere',color:IOS.label2,fontSize:'0.65rem'}}>{row.observacion}</span></td>
                            <td style={{padding:'7px 8px'}}><span style={{display:'block',whiteSpace:'normal',overflowWrap:'anywhere',color:IOS.label2,fontSize:'0.65rem'}}>{row.obsResumen}</span></td>
                            <td style={{padding:'4px',textAlign:'center'}}>
                              <Tooltip title="Ver detalle" placement="top">
                                <Box onClick={()=>verDetalle(row)}
                                  sx={{width:26,height:26,borderRadius:'50%',bgcolor:`${IOS.blue}12`,display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer','&:hover':{bgcolor:`${IOS.blue}22`}}}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                </Box>
                              </Tooltip>
                            </td>
                            <td style={{padding:'4px',textAlign:'center'}}>
                              <Tooltip title="Imprimir" placement="top">
                                <Box onClick={()=>imprimir(row)}
                                  sx={{width:26,height:26,borderRadius:'50%',bgcolor:`${IOS.green}12`,display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:'pointer','&:hover':{bgcolor:`${IOS.green}22`}}}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                                </Box>
                              </Tooltip>
                            </td>
                            <td style={{padding:'4px',textAlign:'center'}}>
                              <Tooltip title={puedeAnular(row)?'Anular':'No anulable'} placement="top">
                                <Box onClick={()=>puedeAnular(row)&&(setFilaSelec(row),setModalAnular(true))}
                                  sx={{width:26,height:26,borderRadius:'50%',bgcolor:'#FFF0EF',display:'inline-flex',alignItems:'center',justifyContent:'center',cursor:puedeAnular(row)?'pointer':'default',opacity:puedeAnular(row)?1:0.3,'&:hover':{bgcolor:puedeAnular(row)?'#FFD4D1':'#FFF0EF'}}}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                </Box>
                              </Tooltip>
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </Box>

                <Paginacion total={datosFiltrados.length} pagina={pagina} setPagina={setPagina} rpp={rpp} setRpp={setRpp} />
              </Box>
          }
        </Box>
      </Box>

      <PaymentReceiptPanel {...{panelOpen,setPanelOpen,filaSelec,detalleVenta,cargandoDet,imprimir,puedeAnular,setModalAnular,esSismed,requiereCpe,descargarDocOriginal,xmlDisponible,cdrDisponible,descargandoDoc}} correoCtx={filaSelec&&['CAJA','DATAFARMA','SISMED'].includes(String(filaSelec.fuente||'').toUpperCase())?{modulo:String(filaSelec.fuente).toUpperCase(),idVenta:filaSelec.codigo,tipoCPE:filaSelec.tipoCPE,serie:filaSelec.serie,numero:filaSelec.numero,ueId,numDoc:filaSelec.dni,cliente:filaSelec.cliente}:null}/>

      {/* Modal Exportar */}
      <Modal open={modalExportar} onClose={()=>!exportando&&setModalExportar(false)} w={460}>
        <ModalHeader title="Exportar Reporte" onClose={()=>!exportando&&setModalExportar(false)} color={IOS.purple}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5"/><path d="M4 20h16"/></svg>} />
        <Box sx={{p:2.5}}>
          <Typography sx={{fontSize:'0.8rem',color:IOS.label2,mb:2}}>
            Se exportarán <strong style={{color:IOS.label}}>{datosFiltrados.length} registros</strong> con los filtros aplicados
          </Typography>
          <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.07em',textTransform:'uppercase',mb:1}}>Detallado</Typography>
          <Box sx={{display:'flex',gap:1.5,mb:2}}>
            {[{fmt:'excel',label:'Excel',ext:'.xlsx',color:IOS.green},{fmt:'pdf',label:'PDF A4',ext:'Horizontal',color:IOS.red}].map(({fmt,label,ext,color})=>(
              <Box key={fmt} component="button" onClick={()=>!exportando&&exportarConFiltros(fmt)} disabled={exportando}
                sx={{flex:1,py:2,borderRadius:'14px',border:`2px solid ${color}30`,bgcolor:`${color}08`,cursor:exportando?'default':'pointer',
                  display:'flex',flexDirection:'column',alignItems:'center',gap:0.8,transition:'all 0.2s',
                  '&:hover':{bgcolor:`${color}14`,borderColor:`${color}60`}}}>
                <Box sx={{width:44,height:44,borderRadius:'12px',bgcolor:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </Box>
                <Typography sx={{fontSize:'0.8rem',fontWeight:700,color}}>{label}</Typography>
                <Typography sx={{fontSize:'0.65rem',color:IOS.label2}}>{ext}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{fontSize:'0.6rem',fontWeight:700,color:IOS.label2,letterSpacing:'0.07em',textTransform:'uppercase',mb:1}}>Resumido por IPRESS</Typography>
          <Box component="button" onClick={()=>!exportando&&exportarConFiltros('pdf-resumido')} disabled={exportando}
            sx={{width:'100%',py:1.5,borderRadius:'14px',border:`2px solid ${IOS.blue}30`,bgcolor:`${IOS.blue}08`,cursor:exportando?'default':'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',gap:1.5,transition:'all 0.2s',
              '&:hover':{bgcolor:`${IOS.blue}14`,borderColor:`${IOS.blue}60`}}}>
            <Box sx={{width:36,height:36,borderRadius:'10px',bgcolor:`${IOS.blue}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/></svg>
            </Box>
            <Box sx={{textAlign:'left'}}>
              <Typography sx={{fontSize:'0.8rem',fontWeight:700,color:IOS.blue}}>PDF Resumido por IPRESS</Typography>
              <Typography sx={{fontSize:'0.65rem',color:IOS.label2}}>Totales agrupados — Vertical A4</Typography>
            </Box>
          </Box>
          {exportando&&<Box sx={{display:'flex',justifyContent:'center',mt:2}}><CircularProgress size={20} sx={{color:IOS.purple}} /></Box>}
        </Box>
      </Modal>

      {/* Modal Formato Impresión */}
      <Modal open={modalFormato&&!!filaImprimir} onClose={()=>!imprimiendo&&setModalFormato(false)} w={380}>
        <ModalHeader title="Formato de Impresión" onClose={()=>!imprimiendo&&setModalFormato(false)} color={IOS.blue}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>} />
        <Box sx={{p:2.5}}>
          {filaImprimir&&(
            <>
              <Typography sx={{fontSize:'0.8rem',color:IOS.label,fontWeight:600,mb:0.3}}>{filaImprimir.comprobante}</Typography>
              <Typography sx={{fontSize:'0.74rem',color:IOS.label2,mb:2}}>{filaImprimir.cliente} — {filaImprimir.fuente}</Typography>
            </>
          )}
          <Box sx={{display:'flex',gap:1.5,mb:1}}>
            {[{fmt:'A4',label:'Formato A4',sub:'Hoja completa',color:IOS.blue},{fmt:'TICKET',label:'Ticket',sub:'80mm / POS',color:IOS.orange}].map(({fmt,label,sub,color})=>(
              <Box key={fmt} component="button" onClick={()=>!imprimiendo&&ejecutarImpresion(fmt)} disabled={imprimiendo}
                sx={{flex:1,py:2.5,borderRadius:'14px',border:`2px solid ${color}30`,bgcolor:`${color}08`,cursor:imprimiendo?'default':'pointer',
                  display:'flex',flexDirection:'column',alignItems:'center',gap:0.8,transition:'all 0.2s',
                  '&:hover':{bgcolor:`${color}14`,borderColor:`${color}60`}}}>
                <Box sx={{width:44,height:44,borderRadius:'12px',bgcolor:`${color}15`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="22" height={fmt==='A4'?28:34} viewBox={fmt==='A4'?"0 0 16 22":"0 0 12 22"} fill="none">
                    {fmt==='A4'
                      ? <><rect x="1" y="1" width="14" height="20" rx="1.5" fill={`${color}20`} stroke={color} strokeWidth="1"/><line x1="4" y1="6" x2="12" y2="6" stroke={color} strokeWidth="1"/><line x1="4" y1="9" x2="12" y2="9" stroke={color} strokeWidth="1"/><line x1="4" y1="12" x2="9" y2="12" stroke={color} strokeWidth="1"/></>
                      : <><rect x="1" y="1" width="10" height="20" rx="1.5" fill={`${color}20`} stroke={color} strokeWidth="1"/><line x1="3" y1="5" x2="9" y2="5" stroke={color} strokeWidth="0.8"/><line x1="3" y1="8" x2="9" y2="8" stroke={color} strokeWidth="0.8"/><line x1="3" y1="11" x2="9" y2="11" stroke={color} strokeWidth="0.8"/></>
                    }
                  </svg>
                </Box>
                <Typography sx={{fontSize:'0.8rem',fontWeight:700,color}}>{label}</Typography>
                <Typography sx={{fontSize:'0.65rem',color:IOS.label2}}>{sub}</Typography>
              </Box>
            ))}
          </Box>
          {imprimiendo&&<Box sx={{display:'flex',justifyContent:'center',mt:1}}><CircularProgress size={20} sx={{color:IOS.blue}} /></Box>}
        </Box>
      </Modal>

      {/* Modal Anular */}
      <Modal open={modalAnular&&!!filaSelec} onClose={()=>!anulando&&setModalAnular(false)} w={380}>
          <Box sx={{position:'relative'}}>
          {estadoCpeAnular && <OverlaySunat estado={estadoCpeAnular}/>}
        <ModalHeader title="Confirmar Anulación" onClose={()=>!anulando&&setModalAnular(false)} color={IOS.red}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>} />
        <Box sx={{p:2.5}}>
          <Typography sx={{color:IOS.label,fontSize:'0.85rem',mb:2,lineHeight:1.6}}>
            ¿Deseas anular <strong>{filaSelec?.comprobante}</strong> de <strong>{filaSelec?.cliente}</strong>?
          </Typography>
          <Box sx={{p:1.5,bgcolor:'#FFF0EF',borderRadius:'10px',border:'1px solid #FFD4D1',mb:2.5}}>
            <Typography sx={{fontSize:'0.76rem',color:'#C0392B',fontWeight:600}}>⚠ Esta acción no se puede deshacer</Typography>
          </Box>
          <Box sx={{display:'flex',gap:1.5}}>
            <Box component="button" onClick={()=>!anulando&&setModalAnular(false)}
              sx={{flex:1,height:36,borderRadius:'10px',border:`1px solid ${IOS.separator}`,bgcolor:IOS.surface,cursor:'pointer',fontSize:'0.82rem',fontWeight:600,color:IOS.label2}}>
              Cancelar
            </Box>
            <Box component="button" onClick={confirmarAnular} disabled={anulando}
              sx={{flex:1,height:36,borderRadius:'10px',border:'none',bgcolor:anulando?'#E5E5EA':IOS.red,cursor:anulando?'default':'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:0.8,transition:'all 0.15s'}}>
              {anulando&&<CircularProgress size={12} sx={{color:'white'}} />}
              <Typography sx={{fontSize:'0.82rem',fontWeight:700,color:anulando?'#8E8E93':'white'}}>{anulando?'Anulando...':'Sí, anular'}</Typography>
            </Box>
          </Box>
        </Box>
            </Box>
      </Modal>
<style>{`
        @keyframes fadeInChart {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </>
  );
};
export default ComprobantesPago;
